import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Ticket } from 'lucide-react'
import { api, ApiError, type PromoRedeemResult } from '../../api/client'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'

interface Props {
  open: boolean
  onClose: () => void
  /**
   * Called after a successful redemption. For entitlement codes the user
   * is now premium and we typically close the paywall too. For discount
   * codes the caller (paywall) uses the returned code on its next
   * createInvoice.
   */
  onRedeemed?: (result: PromoRedeemResult) => void
}

/**
 * Single modal for both kinds of promo codes. The same /api/promo/redeem
 * endpoint handles either flavour — service decides which path to take
 * from the code's row in the DB.
 */
export function PromoCodeModal({ open, onClose, onRedeemed }: Props) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [code, setCode] = useState('')
  const [success, setSuccess] = useState<PromoRedeemResult | null>(null)

  const redeem = useMutation({
    mutationFn: (raw: string) => api.redeemPromo(raw),
    onSuccess: (result) => {
      setSuccess(result)
      if (result.kind === 'entitlement_period') {
        // Premium just got granted — refresh anything entitlement-driven.
        qc.invalidateQueries({ queryKey: ['natal'] })
        qc.invalidateQueries({ queryKey: ['horoscope'] })
        qc.invalidateQueries({ queryKey: ['daily'] })
        qc.invalidateQueries({ queryKey: ['chat'] })
      }
      onRedeemed?.(result)
    },
  })

  const handleClose = () => {
    setCode('')
    setSuccess(null)
    redeem.reset()
    onClose()
  }

  const trimmed = code.trim()
  const canSubmit = trimmed.length >= 4 && !redeem.isPending

  return (
    <Modal open={open} onClose={handleClose} title={t('promo.title')}>
      <div className="flex flex-col gap-3">
        {!success && (
          <>
            <p className="text-sm text-sage-700 leading-relaxed">{t('promo.hint')}</p>
            <div className="flex items-center gap-2 rounded-2xl border border-sage-200 bg-white/80 px-3 py-2">
              <Ticket className="h-5 w-5 shrink-0 text-copper" strokeWidth={1.6} aria-hidden />
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 64))}
                placeholder="YDVG-XXXX-XXXX"
                autoFocus
                className="flex-1 bg-transparent text-sage-800 placeholder-sage-400 outline-none font-mono text-sm tracking-wider"
              />
            </div>
            {redeem.isError && (
              <p className="text-sm text-copper">
                {redeem.error instanceof ApiError
                  ? redeem.error.body.error.message
                  : (redeem.error as Error).message}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="md" onClick={handleClose}>
                {t('actions.cancel')}
              </Button>
              <Button
                variant="primary"
                size="md"
                loading={redeem.isPending}
                disabled={!canSubmit}
                onClick={() => redeem.mutate(trimmed)}
              >
                {t('promo.apply')}
              </Button>
            </div>
          </>
        )}

        {success && success.kind === 'entitlement_period' && (
          <div className="flex flex-col gap-3">
            <div className="rounded-2xl bg-[#eaf5ec] p-4 ring-1 ring-[#a8d6b1]/60">
              <p className="font-serif text-base text-sage-800">
                {t('promo.success_entitlement', { days: success.grantedDays })}
              </p>
              {success.currentPeriodEnd && (
                <p className="mt-1 text-xs text-sage-600">
                  {t('promo.until', {
                    date: new Date(success.currentPeriodEnd).toLocaleDateString(),
                  })}
                </p>
              )}
            </div>
            <Button variant="primary" size="md" onClick={handleClose}>
              {t('actions.done')}
            </Button>
          </div>
        )}

        {success && success.kind === 'discount' && (
          <div className="flex flex-col gap-3">
            <div className="rounded-2xl bg-[#fbeadd] p-4 ring-1 ring-[#e6c4a4]/60">
              <p className="font-serif text-base text-sage-800">
                {t('promo.success_discount', { percent: success.discountPercent })}
              </p>
              <p className="mt-1 text-xs text-sage-600">{t('promo.success_discount_hint')}</p>
            </div>
            <Button variant="primary" size="md" onClick={handleClose}>
              {t('actions.continue')}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  )
}
